import { useModalAction } from '@components/common/modal/modal.context'
import { DEFAULT_CODE, STOCK_TYPE } from '@constants/index'
import { UserContext } from '@contexts/user/user.context'
import { IUser } from '@contexts/user/user.utils'
import { getShipInDays, isSpecialOrder } from '@utils/helper'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useContext } from 'react'

interface IProductTypeProps {
  data: {
    stock_type?: string
    ships_in_days?: number
    shipping_type?: string
    flags?: string[]
  }
  expectedDelivery: string
  showZipChange: boolean
}

export default function ProductType({
  data,
  expectedDelivery,
  showZipChange,
}: IProductTypeProps) {
  const { shippingInformation } = useContext(UserContext) as IUser
  const { postcode } = shippingInformation
  const { t: translate } = useTranslation('common')
  const { openModal } = useModalAction()

  const shipsInDays = getShipInDays(data?.ships_in_days, translate)
  const isSpecialOrderFlag = isSpecialOrder(data)

  const stockImages = {
    Stocked: '/assets/images/stocked-item.svg',
    'On Request': '/assets/images/on-request.svg',
    specialOrder: '/assets/images/special-order.svg',
  }

  const handleChangeZip = () => {
    openModal('CHANGE_ZIP')
  }

  const renderShippingDays = () =>
    !expectedDelivery ? (
      <p className="text-sm text-brand ">
        {translate('text-ready-to-ship')}{' '}
        <span
          className={`${
            isSpecialOrderFlag ? 'text-yellow' : 'text-brand-green'
          }`}
        >
          {shipsInDays?.value} {shipsInDays?.unit}(s)
        </span>
      </p>
    ) : null

  return (
    <>
      {data?.stock_type && !data?.flags?.includes('special_order') && (
        <div className="my-3 flex">
          {data.stock_type === STOCK_TYPE.STOCKED && (
            <Image
              src={stockImages.Stocked}
              alt="Stocked Item"
              width={40}
              height={40}
            />
          )}
          {data.stock_type === STOCK_TYPE.ON_REQUEST && (
            <Image
              src={stockImages['On Request']}
              alt="On Request"
              width={40}
              height={40}
            />
          )}
          {data.stock_type === STOCK_TYPE.SPECIAL_ORDER && (
            <Image
              src={stockImages['specialOrder']}
              alt="Special Order"
              width={40}
              height={40}
            />
          )}
          <div className="pl-2">
            <span className="text-brand font-medium">{data.stock_type}</span>
            {renderShippingDays()}
          </div>
        </div>
      )}

      {data?.flags?.includes('special_order') && (
        <div className="my-3 flex">
          <Image
            src={stockImages.specialOrder}
            alt="Special Order"
            width={40}
            height={40}
          />
          <div className="pl-2">
            <span className="text-brand font-medium">Special Order</span>
            {renderShippingDays()}
          </div>
        </div>
      )}

      <div className="text-sm">
        <span className="pr-2 font-medium text-brand-gray text-[16px]">
          {translate('text-deliver-to')} {postcode ? postcode : DEFAULT_CODE}
        </span>
        {showZipChange && (
          <span
            className="text-brand font-medium underline hover:cursor-pointer hover:no-underline"
            onClick={handleChangeZip}
          >
            {translate('text-change')}?
          </span>
        )}
      </div>
    </>
  )
}
